<template>
	<div class="cms-button-block">
		<a
			:href="url || '#'"
			class="cms-button-block__btn"
			:class="[
				'cms-button-block__btn--' + size,
				'cms-button-block__btn--' + resolvedVariant,
				{ 'cms-button-block__btn--full': isFullWidth }
			]"
			:style="customStyle"
		>
			<i v-if="icon" :class="'fa fa-' + icon" class="cms-button-block__icon"></i>
			{{ label }}
		</a>
	</div>
</template>

<script>
/**
 * ButtonBlock.vue
 * ───────────────
 * A call-to-action button. Primary and Secondary follow the Site Design
 * button tokens (--site-btn-*), so one change restyles every button; custom
 * colors on the block override them. Alignment is a wrapper style trait:
 * the button is inline-flex so the wrapper's text-align positions it
 * (unless fullWidth is set).
 */

export default {
	name: 'ButtonBlock',

	props: {
		label: { type: String, default: 'Click Me' },
		url: { type: String, default: '#' },
		backgroundColor: { type: String, default: '' },
		textColor: { type: String, default: '' },
		borderColor: { type: String, default: '' },
		variant: {
			type: String,
			default: 'primary',
			validator: function (v) { return ['primary', 'secondary', 'success', 'danger', 'warning', 'dark', 'outline', 'custom'].indexOf(v) !== -1; }
		},
		size: {
			type: String,
			default: 'medium',
			validator: function (v) { return ['small', 'medium', 'large'].indexOf(v) !== -1; }
		},
		fullWidth: {
			type: [Boolean, String],
			default: false
		},
		icon: { type: String, default: '' },
		borderRadius: { type: String, default: '' }
	},

	computed: {
		isFullWidth: function () {
			return this.fullWidth === true || this.fullWidth === 'true';
		},

		/** Legacy 'outline' and 'custom' map onto the token-driven secondary look. */
		resolvedVariant: function () {
			if (this.variant === 'outline' || this.variant === 'custom') return 'secondary';
			return this.variant || 'primary';
		},

		customStyle: function () {
			var s = {};
			if (this.borderRadius) s.borderRadius = this.borderRadius;
			// Custom colors override variant
			if (this.backgroundColor) s.backgroundColor = this.backgroundColor;
			if (this.textColor) s.color = this.textColor;
			if (this.borderColor) s.borderColor = this.borderColor;
			return s;
		}
	}
};
</script>

<style scoped>
.cms-button-block {
	width: 100%;
	max-width: 100%;
	text-align: inherit;
}
.cms-button-block__btn {
	display: inline-flex; align-items: center; gap: 0.5rem; vertical-align: middle;
	text-decoration: none; cursor: pointer;
	font-family: var(--site-btn-font, inherit);
	font-weight: var(--site-btn-font-weight, 600);
	text-transform: var(--site-btn-transform, none);
	border-radius: var(--site-btn-radius, 4px);
	border: 2px solid transparent; transition: opacity 0.15s;
}
.cms-button-block__btn:hover { opacity: 0.85; }

/* Full width */
.cms-button-block__btn--full {
	display: flex;
	width: 100%;
	justify-content: center;
}

/* Sizes */
.cms-button-block__btn--small  { padding: 0.35rem 1rem; font-size: 1em; }
.cms-button-block__btn--medium { padding: 0.6rem 1.5rem; font-size: 1em; }
.cms-button-block__btn--large  { padding: 0.8rem 2rem; font-size: 1.1em; }

/* Variants */
.cms-button-block__btn--primary { background: var(--site-btn-primary-bg, #FB6A00); color: var(--site-btn-primary-color, #FFF); border-color: var(--site-btn-primary-border, var(--site-btn-primary-bg, #FB6A00)); }
.cms-button-block__btn--secondary { background: var(--site-btn-secondary-bg, #FFF); color: var(--site-btn-secondary-color, #1E2031); border-color: var(--site-btn-secondary-border, #DDDEE0); }
.cms-button-block__btn--success { background: #009961; color: #FFF; }
.cms-button-block__btn--danger  { background: #E31515; color: #FFF; }
.cms-button-block__btn--warning { background: #EEBC00; color: #1E2031; }
.cms-button-block__btn--dark    { background: #1E2031; color: #FFF; }
.cms-button-block__btn--outline { background: transparent; color: #1E2031; border-color: #1E2031; }
</style>
